import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import _, { constant, initial } from 'lodash';
import { DocumentNode, OperationDefinitionNode } from 'graphql';
import recursiveKeyFilter from 'recursive-key-filter';
import { FormProvider, Path, UseFormReturn, useForm } from 'react-hook-form';
import {
  QueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  defaultContext,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import gql from 'graphql-tag';
import { Maybe } from 'graphql/jsutils/Maybe';

import { mergeDeep } from './merge-deep';
import { FieldError } from '../components/RatesTool/types';

export interface DefinitionsObjectType {
  queryVariablesType: any;
  queryResultType: any;
  mutationVariablesType: any;
  mutationResultType: any;
}

export interface QueryHook<DefinitionsObject extends DefinitionsObjectType> {
  document: string;
  getKey(
    variables?: DefinitionsObject['queryVariablesType']
  ): (string | DefinitionsObject['queryVariablesType'])[];
  fetcher(
    variables?: DefinitionsObject['queryVariablesType'],
    options?: RequestInit['headers']
  ): () => Promise<DefinitionsObject['queryResultType']>;
}

export interface MutationHook<DefinitionsObject extends DefinitionsObjectType> {
  getKey(
    variables?: DefinitionsObject['mutationVariablesType']
  ): (string | DefinitionsObject['mutationVariablesType'])[];
  fetcher(
    variables?: DefinitionsObject['mutationVariablesType'],
    options?: RequestInit['headers']
  ): () => Promise<DefinitionsObject['mutationResultType']>;
}

export const getOperationName = (document: DocumentNode) => {
  const operationDefinition = _.find(document.definitions, (definition) => {
    return definition.kind === 'OperationDefinition';
  });

  return (operationDefinition as OperationDefinitionNode)?.name?.value;
};

/**
 * Remove __typename from a GraphQL response or object.
 *
 * @param {Object|Array} obj object to remove __typename from
 * @return {Object} object same as input but without the __typename
 */
function removeTypename(obj: any) {
  if (typeof obj !== 'object') {
    throw new Error('Provided input is not an object or array.');
  }

  return recursiveKeyFilter(obj, ['__typename']);
}

function formatFieldErrors(
  errors: Maybe<any>[],
  { prefix = '', skipCamelCasing = false } = {}
) {
  let nested = false;

  const hasTopLevelAddressError = errors.find(
    (error) => error.field === `address`
  );
  const hasAnotherAddressError = errors.find((error) =>
    error.field.includes('address.')
  );
  return errors.reduce((acc: { [key: string]: string }, err: FieldError) => {
    if (err && err.field) {
      const field = err.field;
      if (err && err.errors && err.errors.length && err.errors[0] !== null) {
        const message = err?.errors[0].message;
        if (message) {
          const fieldName = skipCamelCasing ? field : _.camelCase(field);
          const prefixedFieldName = prefix
            ? `${prefix}.${fieldName}`
            : fieldName;
          acc[prefixedFieldName] = message;
        }
      }
    }
    return acc;
  }, {});
}

export interface SmartFormProps<
  DefinitionsObject extends DefinitionsObjectType,
  WizardEnum extends Record<string, string>,
> {
  wizard?: UseWizardReturn<WizardEnum>;
  wizardStep?: EnumValueNames<WizardEnum>;
  queryOptions?: UseQueryOptions<DefinitionsObject['queryResultType']>;
  mutationOptions?: UseMutationOptions<DefinitionsObject['mutationResultType']>;
  queryVariables: DefinitionsObject['queryVariablesType'];
  initialValues: DefinitionsObject['mutationVariablesType'];
  queryHook: QueryHook<DefinitionsObject>;
  mutationHook: MutationHook<DefinitionsObject>;
  /** When true, the form is hydrated from the query only once per query key.
   * Subsequent refetches for the same key are ignored so in-progress edits are
   * not wiped. Use this for forms where the user may be editing while background
   * refetches occur (e.g. polling or focus-refetch). */
  hydrateOnce?: boolean;
}

export type UseSmartFormReturn<
  DefinitionsObject extends DefinitionsObjectType,
> = {
  isLoading: boolean;
  queryInfo: UseQueryResult<DefinitionsObject['queryResultType'], any>;
  mutationInfo: UseMutationResult<
    DefinitionsObject['mutationResultType'],
    any,
    DefinitionsObject['mutationVariablesType'],
    any
  >;
  SmartForm: React.FC<{
    render: () => ReactNode;
  }>;
  form: UseFormReturn<
    DefinitionsObject['mutationVariablesType'],
    any,
    undefined
  >;
  onSubmit: (
    e?: React.BaseSyntheticEvent<object, any, any> | undefined
  ) => Promise<void>;
};

export function useSmartForm<
  DefinitionsObject extends DefinitionsObjectType,
  WizardEnum extends Record<string, string>,
>({
  wizard,
  wizardStep,
  queryVariables,
  initialValues,
  queryOptions = {},
  mutationOptions = {},
  queryHook,
  mutationHook,
  hydrateOnce = false,
}: SmartFormProps<
  DefinitionsObject,
  WizardEnum
>): UseSmartFormReturn<DefinitionsObject> {
  const queryClient = useQueryClient({ context: defaultContext });
  const operationName = getOperationName(gql(queryHook.document));
  if (wizard && !wizardStep) {
    throw new Error('Must provide wizard step for useSmartForm');
  }

  const wizardState = wizard?.currentState;
  const isInitialWizardStep =
    wizard && wizardStep && wizard?.defaultStep === wizardStep;

  let queryEnabled = true;

  if (wizard && !isInitialWizardStep) {
    queryEnabled = false;
  }

  const isQueryFetchEnabled =
    queryEnabled &&
    (typeof queryOptions.enabled === 'boolean' ? queryOptions.enabled : true);

  const formMethods = useForm<DefinitionsObject['mutationVariablesType']>({
    defaultValues: initialValues,
  });

  // When hydrateOnce is true, track the last hydrated key so subsequent
  // refetches for the same query do not reset in-progress edits.
  const lastHydratedQueryKeyRef = useRef<string | null>(null);

  function queryOnSuccessHandler(
    result: DefinitionsObject['queryResultType'],
    opts?: { force?: boolean }
  ) {
    if (!operationName) {
      return;
    }
    const formValues = removeTypename({
      data: result[operationName].formValues,
    });
    if (hydrateOnce && !opts?.force) {
      const hydrationKey = JSON.stringify(queryHook.getKey(queryVariables));
      if (lastHydratedQueryKeyRef.current === hydrationKey) {
        return;
      }
      lastHydratedQueryKeyRef.current = hydrationKey;
    }
    formMethods.reset(formValues);
  }

  const queryOptionsToUse: UseQueryOptions<
    DefinitionsObject['queryResultType']
  > = {
    ...queryOptions,
    context: defaultContext,
    refetchOnWindowFocus: false,
    // Honor caller `enabled` (e.g. skip autoform query until an edit id exists).
    enabled: isQueryFetchEnabled,

    onSuccess: (result) => {
      queryOnSuccessHandler(result);
    },
  };

  const queryInfo = useQuery<
    DefinitionsObject['queryResultType'],
    any,
    DefinitionsObject['queryResultType']
  >(
    queryHook.getKey(queryVariables),
    queryHook.fetcher(queryVariables, {}),
    queryOptionsToUse
  );

  const mutationOptionsToUse: UseMutationOptions<
    DefinitionsObject['mutationResultType']
  > = {
    context: defaultContext,
    ...mutationOptions,
  };

  const mutationInfo = useMutation<
    DefinitionsObject['mutationResultType'],
    any,
    DefinitionsObject['mutationVariablesType'],
    any
  >(
    mutationHook.getKey(),
    (variables?: DefinitionsObject['mutationVariablesType']) =>
      mutationHook.fetcher(variables, {})(),
    mutationOptionsToUse
  );

  // When the query is disabled, ignore query loading flags. Otherwise "add" autoform
  // flows (enabled: false) can still see isFetching/isLoading from the observer and
  // block the UI (spinner / disabled fields) even though no fetch runs.
  const isLoading =
    (isQueryFetchEnabled &&
      (queryInfo.isLoading ||
        queryInfo.isFetching ||
        queryInfo.isRefetching)) ||
    mutationInfo.isLoading;

  const onSubmit = formMethods.handleSubmit(
    async (data) => {
      const submitData = mergeDeep(initialValues, data);

      const resp = await mutationInfo.mutateAsync(submitData);
      // debugger;
      const firstKey = _.first(_.keys(resp));

      if (firstKey && resp[firstKey]?.__typename === 'Errors') {
        if (resp[firstKey].fieldErrors) {
          const errors = formatFieldErrors(
            // @ts-ignore
            resp[firstKey].fieldErrors,
            { prefix: 'data', skipCamelCasing: true }
          );

          Object.keys(errors).forEach((key) => {
            formMethods.setError(
              key as Path<DefinitionsObject['mutationVariablesType']>,
              { type: 'custom', message: errors[key] },
              { shouldFocus: false }
            );
          });
        }
      } else if (wizard && firstKey) {
        const nextStep = resp[firstKey].nextStep;
        const nextFormData = resp[firstKey].nextFormData;

        // const nextKey = props.steps[nextStep].component.queryOperationName;
        //   const nextFormData = {
        //     [nextKey]: response[firstKey].nextFormData as object,
        //   };

        if (nextStep) {
          wizard.setCurrentState((prev) => ({
            ...prev,
            currentStep: nextStep,
            currentStepFormData: nextFormData,
          }));
        } else {
          wizard.onComplete();
        }
      }
    },
    (error) => {
      console.log('ERROR SUBMITTING', error);
    }
  );

  const SmartForm: React.FC<{ render: () => ReactNode }> = (props) => {
    return (
      <>
        <FormProvider {...formMethods}>
          <form onSubmit={onSubmit}>{props.render()}</form>
        </FormProvider>
      </>
    );
  };

  useEffect(() => {
    if (
      wizard &&
      wizardState?.currentStep === wizardStep &&
      !isInitialWizardStep
    ) {
      const queryData = {
        [operationName as string]: removeTypename(
          wizardState?.currentStepFormData
        ),
      };

      queryClient.setQueryData(queryHook.getKey(queryVariables), queryData);
      queryOnSuccessHandler(queryData, { force: true });
    }
  }, []);

  return {
    queryInfo,
    mutationInfo,
    SmartForm,
    form: formMethods,
    onSubmit,
    isLoading,
  };
}

export interface UseWizardProps<EnumObject extends Record<string, string>> {
  enumObject: EnumObject;
  defaultStep: EnumObject[keyof EnumObject];
  onComplete: () => void;
}

export type EnumValueNames<EnumObject extends Record<string, string>> =
  EnumObject[keyof EnumObject];
export interface CurrentStateShape<WizardStepNames> {
  currentStepFormData: object | null | undefined;
  currentStep: WizardStepNames;
}

export interface UseWizardReturn<EnumObject extends Record<string, string>> {
  enumObject: EnumObject;
  currentState: CurrentStateShape<EnumValueNames<EnumObject>>;
  setCurrentState: React.Dispatch<
    React.SetStateAction<CurrentStateShape<EnumValueNames<EnumObject>>>
  >;
  defaultStep: EnumValueNames<EnumObject>;
  onComplete: () => void;
}

export function useWizard<EnumObject extends Record<string, string>>({
  defaultStep,
  onComplete,
  enumObject,
}: UseWizardProps<EnumObject>): UseWizardReturn<EnumObject> {
  const [currentState, setCurrentState] = useState<
    CurrentStateShape<EnumObject[keyof EnumObject]>
  >({
    currentStepFormData: null,
    currentStep: defaultStep,
  });

  return { currentState, enumObject, setCurrentState, defaultStep, onComplete };
}

export type DelayedRenderProps = {
  children: React.ReactNode;
  waitBeforeShow?: number;
};

export const DelayedRender: React.FC<DelayedRenderProps> = ({
  children,
  waitBeforeShow = 500,
}) => {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsShown(true);
    }, waitBeforeShow);
  }, [waitBeforeShow]); // eslint-disable-line

  return isShown ? <>{children}</> : null;
};
